import { signOut } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Capacitor } from '@capacitor/core';

/**
 * 로그아웃 관련 로직을 담당하는 커스텀 훅
 * @param {function} setIsLoggedIn - 로그인 상태 설정 함수
 * @param {function} setUserEmail - 사용자 이메일 설정 함수
 * @param {function} setLogoutDialogOpen - 로그아웃 다이얼로그 상태 설정 함수
 * @param {function} resetEmailForm - 이메일 폼 초기화 함수
 * @returns {object} - 로그아웃 관련 함수들
 */
export const useLogout = (setIsLoggedIn, setUserEmail, setLogoutDialogOpen, resetEmailForm) => {
  
  /**
   * 🚨 CRITICAL SECURITY FIX: FCM 토큰 정리 함수
   * 로그아웃 시 반드시 호출되어야 하는 보안 필수 로직
   */
  const cleanupFCMTokens = async (userId) => {
    if (!userId) {
      console.warn('⚠️ [Security] FCM 토큰 정리 실패: userId 없음');
      return;
    }

    try {
      console.log('🔒 [Security] FCM 토큰 정리 시작 - 사용자:', userId);
      
      // 현재 디바이스의 FCM 토큰 해제 요청
      const pendingToken = sessionStorage.getItem('pendingFCMToken');
      let currentToken = null;
      
      if (pendingToken) {
        try {
          const tokenData = JSON.parse(pendingToken);
          currentToken = tokenData.token;
        } catch (e) {
          console.error('❌ [Security] 대기 중인 토큰 파싱 실패:', e);
        }
      }
      
      // 네이티브 플랫폼에서 현재 FCM 토큰 가져오기
      if (!currentToken && Capacitor.isNativePlatform()) {
        try {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          // 현재 토큰이 없으면 플러그인에서 직접 가져오기 시도하지 않음
          // (이미 로그아웃 중이므로 토큰 갱신하지 않음)
        } catch (e) {
          console.error('❌ [Security] 네이티브 FCM 토큰 확인 실패:', e);
        }
      }
      
      // 백엔드에 토큰 해제 요청 (현재 토큰이 있는 경우)
      if (currentToken) {
        try {
          const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
          const response = await fetch(`${API_URL}/api/v1/fcm/unregister-token`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'user-id': userId
            },
            body: JSON.stringify({ token: currentToken })
          });
          
          if (response.ok) {
            console.log('✅ [Security] FCM 토큰 해제 성공');
          } else {
            console.error('❌ [Security] FCM 토큰 해제 실패:', response.status);
          }
        } catch (e) {
          console.error('❌ [Security] FCM 토큰 해제 요청 실패:', e);
        }
      }
      
      // 🚨 CRITICAL: 현재 디바이스의 모든 활성 토큰 비활성화 요청
      try {
        const platform = Capacitor.getPlatform();
        
        // 🚨 SECURITY FIX: 새로운 디바이스 ID 방식 사용
        const deviceId = localStorage.getItem('tubedigest_device_id') || `${userId}_${platform}`;
        
        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_URL}/api/v1/fcm/cleanup-device-tokens`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'user-id': userId
          },
          body: JSON.stringify({ 
            deviceId: deviceId,
            platform: platform 
          })
        });
        
        if (response.ok) {
          console.log('✅ [Security] 디바이스 토큰 정리 성공');
        } else {
          console.error('❌ [Security] 디바이스 토큰 정리 실패:', response.status);
        }
      } catch (e) {
        console.error('❌ [Security] 디바이스 토큰 정리 요청 실패:', e);
      }
      
      // 로컬 저장소 정리 (디바이스 ID는 유지 - 물리적 디바이스 식별용)
      sessionStorage.removeItem('pendingFCMToken');
      localStorage.removeItem('fcmTokenRegistered');
      // localStorage.removeItem('tubedigest_device_id'); // 🚨 디바이스 ID는 유지 (재사용)
      
      console.log('✅ [Security] FCM 토큰 정리 완료');
      
    } catch (error) {
      console.error('❌ [Security] FCM 토큰 정리 중 오류:', error);
      // 토큰 정리 실패해도 로그아웃은 계속 진행 (보안상 중요하지만 사용자 경험을 막지 않음)
    }
  };

  /**
   * 로그아웃 처리 함수
   * @returns {Promise<boolean>} - 성공 여부
   */
  const handleLogout = async () => {
    try {
      // 현재 사용자 ID 저장 (FCM 토큰 정리용)
      const currentUserId = auth.currentUser?.uid;
      
      // 카카오 로그아웃
      if (window.Kakao?.Auth?.getAccessToken()) {
        try {
          await window.Kakao.Auth.logout();
          console.log("카카오 로그아웃 성공");
        } catch (kakaoError) {
          console.error("카카오 로그아웃 중 오류:", kakaoError);
          // 카카오 로그아웃 실패해도 계속 진행
        }
      }
      
      // 🚨 CRITICAL SECURITY FIX: FCM 토큰 정리 (Firebase 로그아웃 전에 실행)
      if (currentUserId) {
        await cleanupFCMTokens(currentUserId);
      }
      
      // Firebase 로그아웃 등 나머지 처리
      await completeLogout();
      return true;
    } catch (e) {
      console.error("로그아웃 중 에러 발생:", e);
      // 에러 메시지를 표시하지 않고 조용히 처리
      await completeLogout();
      return true;
    }
  };

  /**
   * 로그아웃 완료 처리 함수
   */
  const completeLogout = async () => {
    localStorage.removeItem('naver_token');

    await signOut(auth);

    setIsLoggedIn(false);
    setUserEmail("");
    setLogoutDialogOpen(false);
    
    // 이메일 폼 상태 완전 초기화
    if (resetEmailForm) {
      resetEmailForm();
    }
  };

  return {
    handleLogout
  };
}; 